
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, isSupabaseConfigured } from './services/supabase';
import { 
  Table, Zone, Reservation, ArenaConfig, User, 
  CategoryDefinition, FloorDimensions, UserRole 
} from './types';
import { 
  INITIAL_CONFIG, INITIAL_TABLES, INITIAL_ZONES, 
  INITIAL_RESERVATIONS, DEFAULT_CATEGORIES, INITIAL_DIMENSIONS, ACTIVE_TENANT_ID 
} from './constants';
import { FloorPlanEditor } from './components/FloorPlanEditor';
import { CustomerView } from './components/CustomerView';
import { AuthModal } from './components/AuthModal';
import { AdminReservationList } from './components/AdminReservationList';
import { AdminCalendarView } from './components/AdminCalendarView';
import { AdminReservationHistory } from './components/AdminReservationHistory';
import { AdminSettings } from './components/AdminSettings';
import { StandardLayout } from './templates/standard/Layout';
import { DemoLayout } from './templates/demo/Layout';
import { LightLayout } from './templates/light/Layout';
import { SuspendedLayout } from './templates/suspended/Layout';

const TENANT_ID = ACTIVE_TENANT_ID;

const NOTIFICATION_SOUND_URL = 'https://blnzzevaweooopnjmvds.supabase.co/storage/v1/object/sign/sound/notification/termin_notification.mp3?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV85MzY5NGM2NC1jYjkwLTQyZDUtYjZlNS1lODFjZmU1ZjQwNDUiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJzb3VuZC9ub3RpZmljYXRpb24vdGVybWluX25vdGlmaWNhdGlvbi5tcDMiLCJpYXQiOjE3NzQ5MDI4OTgsImV4cCI6MTg2OTUxMDg5OH0.CAOCm6YOMFYffAgtNSWMjEmlJoc_7ThpqqQ3AAxUIH8';

// ==========================================================================
// iOS/iPad-compatible Notification Sound System
// ==========================================================================
// iOS Safari blocks audio playback until a user gesture has occurred.
// Strategy:
//   1) On the FIRST user touch/click anywhere, we "unlock" the AudioContext
//      by playing a silent buffer, and pre-fetch + decode the notification MP3.
//   2) Subsequent calls to playNotificationSound() use the already-decoded
//      AudioBuffer → instant, reliable playback on all platforms.
// ==========================================================================

class NotificationSoundManager {
  private audioContext: AudioContext | null = null;
  private audioBuffer: AudioBuffer | null = null;
  private unlocked = false;
  private fetchPromise: Promise<void> | null = null;

  /** Call this once from a click/touch handler to unlock iOS audio. */
  unlock() {
    if (this.unlocked) return;
    this.unlocked = true;

    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    this.audioContext = new Ctx();

    // iOS requires us to play *something* inside the user gesture
    const silent = this.audioContext!.createBuffer(1, 1, 22050);
    const src = this.audioContext!.createBufferSource();
    src.buffer = silent;
    src.connect(this.audioContext!.destination);
    src.start(0);

    // Pre-fetch and decode the MP3 in the background
    this.fetchPromise = fetch(NOTIFICATION_SOUND_URL)
      .then(r => r.arrayBuffer())
      .then(buf => this.audioContext!.decodeAudioData(buf))
      .then(decoded => { this.audioBuffer = decoded; })
      .catch(err => console.warn('Notification sound pre-load failed:', err));
  }

  /** Play the notification sound. Safe to call anytime – no-op if not unlocked. */
  async play() {
    if (!this.audioContext || !this.unlocked) return;

    // Resume context if it was suspended (e.g. tab was backgrounded)
    if (this.audioContext.state === 'suspended') {
      try { await this.audioContext.resume(); } catch {}
    }

    // Wait for fetch if still in progress
    if (this.fetchPromise && !this.audioBuffer) {
      await this.fetchPromise;
    }

    if (!this.audioBuffer) return;

    const source = this.audioContext.createBufferSource();
    source.buffer = this.audioBuffer;
    source.connect(this.audioContext.destination);
    source.start(0);
  }
}

const notificationSound = new NotificationSoundManager();

// --- SECURE ID GENERATION ---
const generateId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
};

// --- MAPPING HELPERS ---

const normalizeTemplate = (tpl: string | null | undefined): 'STANDARD' | 'LIGHT' | 'DEMO' | 'SUSPENDED' => {
    if (!tpl) return 'STANDARD';
    const upper = tpl.toUpperCase().trim();
    if (upper === 'DEMO' || upper === 'LIGHT' || upper === 'SUSPENDED') return upper;
    return 'STANDARD';
};

const mapConfigFromDB = (dbConfig: any): ArenaConfig => ({
  ...INITIAL_CONFIG,
  tenantId: dbConfig.tenant_id, 
  name: dbConfig.name,
  layoutTemplate: normalizeTemplate(dbConfig.layout_template),
  address: dbConfig.address,
  phoneLandline: dbConfig.phone_landline,
  phoneMobile: dbConfig.phone_mobile,
  contactEmail: dbConfig.contact_email,
  websiteUrl: dbConfig.website_url,
  instagramUrl: dbConfig.instagram_url,
  facebookUrl: dbConfig.facebook_url,
  twitterUrl: dbConfig.twitter_url,
  tiktokUrl: dbConfig.tiktok_url,
  ownerName: dbConfig.owner_name,
  showOwnerOnStart: dbConfig.show_owner_on_start ?? true,
  logoUrl: dbConfig.logo_url,
  welcomeText: dbConfig.welcome_text,
  schedule: dbConfig.schedule || INITIAL_CONFIG.schedule,
  guestViewMode: dbConfig.guest_view_mode || 'SIMPLE',
  reservationMode: dbConfig.reservation_mode || 'SPECIFIC_CATEGORY',
  lastReservationBuffer: dbConfig.last_reservation_buffer || 30,
  allowGuestReservations: dbConfig.allow_guest_reservations ?? true,
  requireReservationApproval: dbConfig.require_reservation_approval ?? false,
  allowRecurringBookings: dbConfig.allow_recurring_bookings ?? true,
  recurringRequireApproval: dbConfig.recurring_require_approval ?? true,
  theme: dbConfig.theme || INITIAL_CONFIG.theme,
  socialProviders: dbConfig.social_providers || [],
  packages: dbConfig.packages || [],
  seo: dbConfig.seo || {}
});

const mapConfigToDB = (config: ArenaConfig) => ({
  tenant_id: config.tenantId,
  name: config.name,
  layout_template: config.layoutTemplate,
  address: config.address,
  phone_landline: config.phoneLandline,
  phone_mobile: config.phoneMobile,
  contact_email: config.contactEmail,
  website_url: config.websiteUrl,
  instagram_url: config.instagramUrl,
  facebook_url: config.facebookUrl,
  twitter_url: config.twitterUrl,
  tiktok_url: config.tiktokUrl,
  owner_name: config.ownerName,
  show_owner_on_start: config.showOwnerOnStart,
  logo_url: config.logoUrl,
  welcome_text: config.welcomeText,
  schedule: config.schedule,
  guest_view_mode: config.guestViewMode,
  reservation_mode: config.reservationMode,
  last_reservation_buffer: config.lastReservationBuffer,
  allow_guest_reservations: config.allowGuestReservations,
  require_reservation_approval: config.requireReservationApproval,
  allow_recurring_bookings: config.allowRecurringBookings,
  recurring_require_approval: config.recurringRequireApproval,
  theme: config.theme,
  social_providers: config.socialProviders,
  packages: config.packages,
  seo: config.seo
});

const mapReservationFromDB = (r: any): Reservation => ({
  id: r.id,
  tenantId: r.tenant_id,
  tableId: r.table_id,
  categoryId: r.category_id,
  packageName: r.package_name,
  userId: r.user_id,
  customerName: r.customer_name,
  phone: r.phone,
  email: r.email,
  partySize: r.party_size,
  date: r.date,
  time: r.time,
  durationMinutes: r.duration_minutes,
  notes: r.notes,
  status: r.status,
  seriesId: r.series_id
});

const mapReservationToDB = (r: Reservation) => ({
  id: r.id,
  tenant_id: r.tenantId,
  table_id: r.tableId,
  category_id: r.categoryId,
  package_name: r.packageName,
  user_id: r.userId,
  customer_name: r.customerName,
  phone: r.phone,
  email: r.email,
  party_size: r.partySize,
  date: r.date,
  time: r.time,
  duration_minutes: r.durationMinutes,
  notes: r.notes,
  status: r.status,
  series_id: r.seriesId
});

const mapCategoryFromDB = (c: any): CategoryDefinition => ({
  id: c.id,
  tenantId: c.tenant_id,
  name: c.name,
  color: c.color,
  borderColor: c.border_color,
  icon: c.icon,
  minDuration: c.min_duration,
  defaultCapacity: c.default_capacity
});

const mapCategoryToDB = (c: CategoryDefinition) => ({
  id: c.id,
  tenant_id: c.tenantId,
  name: c.name,
  color: c.color,
  border_color: c.borderColor,
  icon: c.icon,
  min_duration: c.minDuration,
  default_capacity: c.defaultCapacity
});

const mapZoneFromDB = (z: any): Zone => ({
  id: z.id,
  tenantId: z.tenant_id,
  name: z.name,
  type: z.type,
  x: z.x,
  y: z.y,
  width: z.width,
  height: z.height,
  customColor: z.custom_color
});

const mapZoneToDB = (z: Zone) => ({
  id: z.id,
  tenant_id: z.tenantId,
  name: z.name,
  type: z.type,
  x: z.x,
  y: z.y,
  width: z.width,
  height: z.height,
  custom_color: z.customColor
});

const mapTableFromDB = (t: any): Table => ({
  id: t.id,
  tenantId: t.tenant_id,
  name: t.name,
  capacity: t.capacity,
  shape: t.shape,
  category: t.category,
  x: t.x,
  y: t.y,
  width: t.width,
  height: t.height,
  rotation: t.rotation
});

const mapTableToDB = (t: Table) => ({
  id: t.id,
  tenant_id: t.tenantId,
  name: t.name,
  capacity: t.capacity,
  shape: t.shape,
  category: t.category,
  x: t.x,
  y: t.y,
  width: t.width,
  height: t.height,
  rotation: t.rotation
});

const App: React.FC = () => {
  const [tables, setTables] = useState<Table[]>(INITIAL_TABLES);
  const [zones, setZones] = useState<Zone[]>(INITIAL_ZONES);
  const [reservations, setReservations] = useState<Reservation[]>(INITIAL_RESERVATIONS);
  const [config, setConfig] = useState<ArenaConfig>(INITIAL_CONFIG);
  const [categories, setCategories] = useState<CategoryDefinition[]>(DEFAULT_CATEGORIES);
  const [dimensions, setDimensions] = useState<FloorDimensions>(INITIAL_DIMENSIONS);
  
  const [configLoaded, setConfigLoaded] = useState(!isSupabaseConfigured);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [adminView, setAdminView] = useState<'EDITOR' | 'RESERVATIONS' | 'CALENDAR' | 'HISTORY'>('EDITOR');
  const [showSettings, setShowSettings] = useState(false);
  const isLoggingOutRef = useRef(false);

  const [acknowledgedIds, setAcknowledgedIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(`acknowledged_${TENANT_ID}`);
      return stored ? new Set<string>(JSON.parse(stored)) : new Set<string>();
    } catch { return new Set<string>(); }
  });

  const handleAcknowledge = useCallback((id: string) => {
    setAcknowledgedIds((prev: Set<string>) => {
      const next = new Set(prev);
      next.add(id);
      try { localStorage.setItem(`acknowledged_${TENANT_ID}`, JSON.stringify([...next])); } catch {}
      return next;
    });
  }, []);

  const isAdmin = currentUser?.role === UserRole.ADMIN;
  const isLoggedIn = !!currentUser || isGuestMode;


  // ================================================================
  // Dynamic SEO: Apply tenant-specific meta tags to document.head
  // ================================================================
  useEffect(() => {
    const seo = config.seo;
    if (!seo) return;

    // Helper: set or create a meta tag
    const setMeta = (attr: string, key: string, content: string | undefined) => {
      if (!content) return;
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    // Helper: set or create a link tag
    const setLink = (rel: string, href: string | undefined) => {
      if (!href) return;
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    // Page title
    if (seo.pageTitle) {
      document.title = seo.pageTitle;
    } else if (config.name) {
      document.title = config.name;
    }

    // Basic meta tags
    setMeta('name', 'description', seo.metaDescription);
    setMeta('name', 'keywords', seo.metaKeywords);
    setMeta('name', 'robots', seo.robotsDirective);
    setMeta('name', 'theme-color', seo.themeColor);

    // Open Graph
    setMeta('property', 'og:title', seo.ogTitle || seo.pageTitle || config.name);
    setMeta('property', 'og:description', seo.ogDescription || seo.metaDescription);
    setMeta('property', 'og:image', seo.ogImage || config.logoUrl);
    setMeta('property', 'og:type', seo.ogType || 'website');
    setMeta('property', 'og:locale', seo.ogLocale || 'de_DE');
    if (seo.canonicalUrl) {
      setMeta('property', 'og:url', seo.canonicalUrl);
    }

    // Twitter Card
    setMeta('name', 'twitter:card', seo.twitterCard || 'summary_large_image');
    setMeta('name', 'twitter:title', seo.twitterTitle || seo.ogTitle || seo.pageTitle || config.name);
    setMeta('name', 'twitter:description', seo.twitterDescription || seo.ogDescription || seo.metaDescription);
    setMeta('name', 'twitter:image', seo.twitterImage || seo.ogImage || config.logoUrl);

    // Canonical URL
    if (seo.canonicalUrl) {
      setLink('canonical', seo.canonicalUrl);
    }

    // Favicon
    if (seo.faviconUrl) {
      setLink('icon', seo.faviconUrl);
      setLink('apple-touch-icon', seo.faviconUrl);
    }

    // Structured Data (JSON-LD)
    if (seo.structuredData) {
      let scriptEl = document.querySelector('script[data-seo-jsonld]') as HTMLScriptElement;
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.setAttribute('type', 'application/ld+json');
        scriptEl.setAttribute('data-seo-jsonld', 'true');
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = seo.structuredData;
    }

    // Custom head tags (raw HTML - admin-only, already trusted)
    if (seo.customHeadTags) {
      let container = document.querySelector('[data-seo-custom]');
      if (!container) {
        container = document.createElement('div');
        container.setAttribute('data-seo-custom', 'true');
        container.setAttribute('style', 'display:none');
        document.head.appendChild(container);
      }
      // Parse and insert only safe tags (meta, link, script with type=application/ld+json)
      const parser = new DOMParser();
      const doc = parser.parseFromString(seo.customHeadTags, 'text/html');
      container.innerHTML = '';
      doc.head.childNodes.forEach(node => {
        if (node instanceof HTMLMetaElement || node instanceof HTMLLinkElement) {
          document.head.appendChild(node.cloneNode(true));
        }
      });
    }

  }, [config.seo, config.name, config.logoUrl]);

  // ================================================================
  // Unlock audio on first user interaction (required for iOS/iPad)
  // ================================================================
  useEffect(() => {
    const unlockAudio = () => {
      notificationSound.unlock();
      // Remove listeners after first interaction
      document.removeEventListener('touchstart', unlockAudio, true);
      document.removeEventListener('click', unlockAudio, true);
    };
    document.addEventListener('touchstart', unlockAudio, { capture: true, once: true });
    document.addEventListener('click', unlockAudio, { capture: true, once: true });
    return () => {
      document.removeEventListener('touchstart', unlockAudio, true);
      document.removeEventListener('click', unlockAudio, true);
    };
  }, []);

  // ================================================================
  // Initial data fetch + Supabase Realtime subscription
  // ================================================================
  useEffect(() => {
    const fetchAllData = async () => {
      if (!isSupabaseConfigured) return;

      try {
        const [configRes, tablesRes, zonesRes, resRes, catRes] = await Promise.all([
          supabase.from('tenants').select('*').eq('tenant_id', TENANT_ID).maybeSingle(),
          supabase.from('tables').select('*').eq('tenant_id', TENANT_ID),
          supabase.from('zones').select('*').eq('tenant_id', TENANT_ID),
          supabase.from('reservation_slots').select('*').eq('tenant_id', TENANT_ID),
          supabase.from('categories').select('*').eq('tenant_id', TENANT_ID),
        ]);

        if (configRes.data) {
            setConfig(mapConfigFromDB(configRes.data));
        } else {
            const { error: seedError } = await supabase.from('tenants').upsert(mapConfigToDB(INITIAL_CONFIG));
            if (!seedError) {
                setConfig(INITIAL_CONFIG);
                const { count: catCount } = await supabase.from('categories').select('*', { count: 'exact', head: true }).eq('tenant_id', TENANT_ID);
                if (catCount === 0) {
                    const dbCats = DEFAULT_CATEGORIES.map(c => ({...mapCategoryToDB(c), tenant_id: TENANT_ID}));
                    await supabase.from('categories').insert(dbCats);
                }
            }
        }
        setConfigLoaded(true);

        if (tablesRes.data?.length) setTables(tablesRes.data.map(mapTableFromDB));
        if (zonesRes.data?.length) setZones(zonesRes.data.map(mapZoneFromDB));
        if (resRes.data) setReservations(resRes.data.map(mapReservationFromDB));
        if (catRes.data?.length) setCategories(catRes.data.map(mapCategoryFromDB));

        // Auto-acknowledge all existing non-PENDING reservations on first load,
        // so only NEW ones arriving via Realtime show up as notifications.
        if (resRes.data) {
          setAcknowledgedIds((prev: Set<string>) => {
            const toAdd = resRes.data!.filter((r: any) => r.status !== 'PENDING' && !prev.has(r.id)).map((r: any) => r.id as string);
            if (toAdd.length === 0) return prev;
            const next = new Set(prev);
            toAdd.forEach((id: string) => next.add(id));
            try { localStorage.setItem(`acknowledged_${TENANT_ID}`, JSON.stringify([...next])); } catch {}
            return next;
          });
        }

      } catch (error) {
        console.error("\u274c Fehler beim Laden der Daten:", error);
        setConfigLoaded(true); // show app even if fetch failed
      }
    };

    fetchAllData();
    
    // ================================================================
    // Supabase Realtime: Listen for reservation changes
    // ================================================================
    let realtimeChannel: ReturnType<typeof supabase.channel> | null = null;

    if (isSupabaseConfigured) {
      realtimeChannel = supabase
        .channel(`reservations-${TENANT_ID}`)
        .on(
          'postgres_changes',
          {
            event: '*',          // INSERT, UPDATE, DELETE
            schema: 'public',
            table: 'reservations',
            filter: `tenant_id=eq.${TENANT_ID}`
          },
          (payload) => {
            const { eventType, new: newRow, old: oldRow } = payload;

            if (eventType === 'INSERT') {
              const newRes = mapReservationFromDB(newRow);
              setReservations(prev => {
                // Avoid duplicates (we might have already added it locally)
                if (prev.some(r => r.id === newRes.id)) return prev;
                return [...prev, newRes];
              });
              // Play notification sound for new reservations
              notificationSound.play();
            }

            if (eventType === 'UPDATE') {
              const updatedRes = mapReservationFromDB(newRow);
              setReservations(prev =>
                prev.map(r => r.id === updatedRes.id ? updatedRes : r)
              );
              // Play sound for status changes (cancellations, approvals, etc.)
              notificationSound.play();
            }

            if (eventType === 'DELETE') {
              const deletedId = oldRow?.id;
              if (deletedId) {
                setReservations(prev => prev.filter(r => r.id !== deletedId));
                notificationSound.play();
              }
            }
          }
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            console.log(`✅ Realtime subscribed for tenant ${TENANT_ID}`);
          }
        });
    }

    // Auth listener
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
        // If we're in the middle of a logout, ignore all auth events
        if (isLoggingOutRef.current) {
            setCurrentUser(null);
            return;
        }
        if (event === 'SIGNED_OUT') {
            setCurrentUser(null);
            return;
        }
        if (session?.user) {
            const metadata = session.user.app_metadata || {};
            const isSuperAdmin = metadata.role === 'SUPER_ADMIN';
            const isTenantAdmin = metadata.roles?.[TENANT_ID] === 'ADMIN';

            // For non-admin users: verify they actually have access to this tenant
            if (!isSuperAdmin && !isTenantAdmin) {
                const { data: tenantAccess } = await supabase
                    .from('user_tenants')
                    .select('tenant_id')
                    .eq('user_id', session.user.id)
                    .eq('tenant_id', TENANT_ID)
                    .maybeSingle();

                if (!tenantAccess) {
                    // No access to this tenant – sign out locally and stop
                    await supabase.auth.signOut({ scope: 'local' });
                    setCurrentUser(null);
                    return;
                }
            }

            const role = (isSuperAdmin || isTenantAdmin) ? UserRole.ADMIN : UserRole.GUEST;
            setCurrentUser({
                id: session.user.id,
                email: session.user.email || '',
                name: session.user.user_metadata?.full_name || session.user.email?.split('@')[0] || 'User',
                role,
                tenantId: TENANT_ID
            });
            setShowAuthModal(false);
            // Authenticated users can read full reservation data (incl. PII) from the restricted table
            const { data: fullResData } = await supabase.from('reservations').select('*').eq('tenant_id', TENANT_ID);
            if (fullResData) setReservations(fullResData.map(mapReservationFromDB));
        } else {
            setCurrentUser(null);
        }
    });

    return () => {
        authListener.subscription.unsubscribe();
        if (realtimeChannel) {
          supabase.removeChannel(realtimeChannel);
        }
    };
  }, []);

  // --- CONFIG ---
  const updateConfig = (newConfig: ArenaConfig) => {
    setConfig(newConfig);
    if (isSupabaseConfigured) {
        supabase.from('tenants').upsert(mapConfigToDB(newConfig)).then(({ error }) => {
            if (error) console.error("Error saving config", error);
        });
    }
  };

  // --- TABLES: Persisted to Supabase ---
  const updateTables = async (newTables: Table[]) => {
    const taggedTables = newTables.map(t => ({ ...t, tenantId: TENANT_ID }));
    setTables(taggedTables);
    if (isSupabaseConfigured) {
        const newIds = new Set(taggedTables.map(t => t.id));
        const deletedIds = tables.filter(t => !newIds.has(t.id)).map(t => t.id);
        
        if (deletedIds.length > 0) {
            const { error: delError } = await supabase
                .from('tables')
                .delete()
                .eq('tenant_id', TENANT_ID)
                .in('id', deletedIds);
            if (delError) console.error("Error deleting tables", delError);
        }
        
        if (taggedTables.length > 0) {
            const dbTables = taggedTables.map(t => mapTableToDB(t));
            const { error } = await supabase.from('tables').upsert(dbTables);
            if (error) console.error("Error saving tables", error);
        }
    }
  };

  // --- ZONES: Persisted to Supabase ---
  const updateZones = async (newZones: Zone[]) => {
    const taggedZones = newZones.map(z => ({ ...z, tenantId: TENANT_ID }));
    setZones(taggedZones);
    if (isSupabaseConfigured) {
        const newIds = new Set(taggedZones.map(z => z.id));
        const deletedIds = zones.filter(z => !newIds.has(z.id)).map(z => z.id);
        
        if (deletedIds.length > 0) {
            const { error: delError } = await supabase
                .from('zones')
                .delete()
                .eq('tenant_id', TENANT_ID)
                .in('id', deletedIds);
            if (delError) console.error("Error deleting zones", delError);
        }
        
        if (taggedZones.length > 0) {
            const dbZones = taggedZones.map(z => mapZoneToDB(z));
            const { error } = await supabase.from('zones').upsert(dbZones);
            if (error) console.error("Error saving zones", error);
        }
    }
  };
  
  // --- CATEGORIES: With delete sync ---
  const updateCategories = async (newCats: CategoryDefinition[]) => {
      const taggedCats = newCats.map(c => ({ ...c, tenantId: TENANT_ID }));
      setCategories(taggedCats);
      if (isSupabaseConfigured) {
         const newIds = new Set(taggedCats.map(c => c.id));
         const deletedIds = categories.filter(c => !newIds.has(c.id)).map(c => c.id);
         
         if (deletedIds.length > 0) {
             const { error: delError } = await supabase
                 .from('categories')
                 .delete()
                 .eq('tenant_id', TENANT_ID)
                 .in('id', deletedIds);
             if (delError) console.error("Error deleting categories", delError);
         }
         
         if (taggedCats.length > 0) {
             const dbCats = taggedCats.map(c => ({...mapCategoryToDB(c), tenant_id: TENANT_ID}));
             const { error } = await supabase.from('categories').upsert(dbCats);
             if (error) console.error("Error saving categories", error);
         }
      }
  };

  // --- RESERVATIONS: All operations include tenant_id filter ---
  const handleReservation = (res: Reservation) => {
    const newRes = { ...res, tenantId: TENANT_ID };
    setReservations(prev => [...prev, newRes]);
    if (isSupabaseConfigured) {
        supabase.from('reservations').insert(mapReservationToDB(newRes)).then(({ error }) => {
            if (error) console.error("Error creating reservation", error);
        });
    }
  };

  const cancelReservation = (id: string) => {
     setReservations(prev => prev.map(r => r.id === id ? { ...r, status: 'CANCELLED_BY_GUEST' } : r));
     if (isSupabaseConfigured) {
         supabase.from('reservations')
             .update({ status: 'CANCELLED_BY_GUEST' })
             .eq('id', id)
             .eq('tenant_id', TENANT_ID)
             .then(({ error }) => {
                if (error) console.error("Error cancelling reservation", error);
             });
     }
  };

  const cancelSeries = (seriesId: string) => {
    setReservations(prev => prev.map(r => r.seriesId === seriesId ? { ...r, status: 'CANCELLED_BY_GUEST' } : r));
    if (isSupabaseConfigured) {
         supabase.from('reservations')
             .update({ status: 'CANCELLED_BY_GUEST' })
             .eq('series_id', seriesId)
             .eq('tenant_id', TENANT_ID)
             .then(({ error }) => {
                if (error) console.error("Error cancelling series", error);
             });
     }
  };

  const handleLogin = (user: User) => {
      setCurrentUser(user);
      setShowAuthModal(false);
  };

  const handleLogout = async () => {
      // Clear UI state immediately so the user sees instant feedback
      setCurrentUser(null);
      setIsGuestMode(false);
      setAdminView('EDITOR');
      isLoggingOutRef.current = true;
      // Sign out and wipe local session, then reload to guarantee clean state
      // (OAuth tokens can live in the URL hash or in-memory — a reload clears all of it)
      try { await supabase.auth.signOut(); } catch {}
      try {
          Object.keys(localStorage)
              .filter(k => k.startsWith('sb-'))
              .forEach(k => localStorage.removeItem(k));
      } catch {}
      window.location.replace(window.location.origin + window.location.pathname);
  };

  const notificationCount = reservations.filter((r: Reservation) =>
    r.status === 'PENDING' ||
    ((r.status === 'CONFIRMED' || r.status === 'CANCELLED_BY_GUEST') && !acknowledgedIds.has(r.id))
  ).length;

  if (!configLoaded) {
    return <div style={{ minHeight: '100vh', background: '#fff' }} />;
  }

  let LayoutComponent = StandardLayout;
  if (config.layoutTemplate === 'DEMO') LayoutComponent = DemoLayout;
  else if (config.layoutTemplate === 'LIGHT') LayoutComponent = LightLayout;
  else if (config.layoutTemplate === 'SUSPENDED') LayoutComponent = SuspendedLayout;

  return (
    <LayoutComponent 
        config={config} 
        currentUser={currentUser} 
        isAdmin={isAdmin} 
        isLoggedIn={isLoggedIn}
        adminView={adminView}
        setAdminView={setAdminView}
        adminNotificationCount={notificationCount}
        onLogout={handleLogout}
        onShowSettings={() => setShowSettings(true)}
    >
        {(!isLoggedIn && !isGuestMode) ? (
            <CustomerView 
                tables={tables}
                reservations={reservations}
                zones={zones}
                onMakeReservation={handleReservation}
                onCancelReservation={cancelReservation}
                dimensions={dimensions}
                config={config}
                categories={categories}
                currentUser={currentUser}
                isGuestMode={isGuestMode}
                setIsGuestMode={setIsGuestMode}
                onLogin={handleLogin}
                onLoginRequest={() => setShowAuthModal(true)}
            />
        ) : isAdmin ? (
            <>
                {adminView === 'EDITOR' && (
                    <FloorPlanEditor 
                        tables={tables} onUpdateTables={updateTables}
                        zones={zones} onUpdateZones={updateZones}
                        dimensions={dimensions} onUpdateDimensions={setDimensions}
                        categories={categories}
                        reservations={reservations}
                        config={config}
                    />
                )}
                {adminView === 'RESERVATIONS' && (
                    <AdminReservationList
                        reservations={reservations}
                        tables={tables}
                        zones={zones}
                        categories={categories}
                        config={config}
                        onMakeReservation={handleReservation}
                        onCancelReservation={cancelReservation}
                        onCancelSeries={cancelSeries}
                        acknowledgedIds={acknowledgedIds}
                        onAcknowledge={handleAcknowledge}
                        onUpdateReservation={(res) => {
                            const taggedRes = { ...res, tenantId: TENANT_ID };
                            setReservations(prev => prev.map(r => r.id === taggedRes.id ? taggedRes : r));
                            if (isSupabaseConfigured) {
                                supabase.from('reservations')
                                    .upsert(mapReservationToDB(taggedRes))
                                    .then(({error}) => {
                                        if(error) console.error("Error updating reservation", error);
                                    });
                            }
                        }}
                    />
                )}
                {adminView === 'CALENDAR' && (
                    <AdminCalendarView 
                        reservations={reservations}
                        tables={tables}
                        categories={categories}
                        config={config}
                        onMakeReservation={handleReservation}
                        onEditReservation={(res) => {
                            const taggedRes = { ...res, tenantId: TENANT_ID };
                            setReservations(prev => prev.map(r => r.id === taggedRes.id ? taggedRes : r));
                            if (isSupabaseConfigured) {
                                supabase.from('reservations')
                                    .upsert(mapReservationToDB(taggedRes))
                                    .then(({error}) => {
                                        if(error) console.error("Error updating reservation", error);
                                    });
                            }
                        }}
                    />
                )}
                {adminView === 'HISTORY' && (
                    <AdminReservationHistory 
                        reservations={reservations}
                        tables={tables}
                        zones={zones}
                        categories={categories}
                    />
                )}
            </>
        ) : (
            <CustomerView 
                tables={tables}
                reservations={reservations}
                zones={zones}
                onMakeReservation={handleReservation}
                onCancelReservation={cancelReservation}
                dimensions={dimensions}
                config={config}
                categories={categories}
                currentUser={currentUser}
                isGuestMode={isGuestMode}
                setIsGuestMode={setIsGuestMode}
                onLogin={handleLogin}
                onLoginRequest={() => setShowAuthModal(true)}
            />
        )}

        {showAuthModal && (
            <AuthModal 
                onLogin={handleLogin} 
                onClose={() => setShowAuthModal(false)} 
                config={config} 
            />
        )}

        {showSettings && (
            <AdminSettings 
                config={config} 
                categories={categories} 
                onUpdateConfig={updateConfig} 
                onUpdateCategories={updateCategories}
                onClose={() => setShowSettings(false)} 
            />
        )}

    </LayoutComponent>
  );
};

export default App;
